/**
 * ComplianceRequirementCard — per-vehicle view-model. Plan §26.4.
 *
 * Mirrors the vendored type at
 *   `vendor/RegPulse-Kaiyang-Contest/lifecycle.ts#ComplianceRequirementCard`.
 *
 * Kaiyang Lesson defenses encoded here:
 *   §25.5 — hard_law cards MUST cite at least one article-or-deeper
 *           LegalReference (superRefine).
 *   §25.6 — `obligationKind` / `obligationTiming` / `legalEffect` are three
 *           INDEPENDENT enums; no merged union.
 *   §25.7 — `jurisdictionCode` is required, non-empty string. Filters and the
 *           coverage explorer read this directly.
 *   §25.9 — `triggeredByFeatures` is annotation-only (no grouping field).
 *   §25.10 — NO `ownerName` / `assigneeId` / `responsibilityPercent` /
 *           `dispatchTo` fields. CI red line #5 (reviewer-trail) keeps this
 *           audit-only.
 *   §25.11 — post_market / recurring / event_triggered / phase_in cards MUST
 *           attach a ContinuingObligation block (superRefine).
 */

import { z } from "zod";
import {
  RegulatoryDomain,
  ObligationKind,
  ObligationTiming,
  ApplicabilityCategory,
  CoverageState,
  LegalEffect,
  LifecycleLayer,
  LifecycleState,
  VerificationMaturityTier,
  ValidationStatus,
} from "./primitives";
import {
  LegalReferenceSchema,
  EvidenceRequirementSchema,
} from "./legal-reference";
import { ContinuingObligationSchema } from "./continuing-obligation";

const ARTICLE_OR_DEEPER_GRANULARITIES = [
  "article",
  "annex",
  "section",
  "chapter",
  "paragraph",
  "subparagraph",
] as const;

const TIMING_REQUIRES_CONTINUING: ReadonlyArray<z.infer<typeof ObligationTiming>> = [
  "post_market",
  "recurring",
  "event_triggered",
  "phase_in",
];

export const ComplianceRequirementCardSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    family: z.string(),
    layer: LifecycleLayer,
    jurisdictionCode: z.string().min(1),
    regulatoryDomain: RegulatoryDomain,
    obligationKind: ObligationKind,
    obligationTiming: ObligationTiming,
    legalEffect: LegalEffect,
    applicabilityCategory: ApplicabilityCategory,
    hiddenByConfigFields: z.array(z.string()).optional(),
    needsConfigFields: z.array(z.string()).optional(),
    legalReferences: z.array(LegalReferenceSchema),
    evidenceGuidance: z.array(EvidenceRequirementSchema),
    coverageState: CoverageState,
    coverageGapReason: z.string().nullable().optional(),
    requirementText: z.string(),
    executionRequirement: z.string(),
    whyAppliesToVehicle: z.string(),
    triggeredByConfigItems: z.array(z.string()),
    triggeredByFeatures: z.array(z.string()),
    linkedObligations: z.array(z.string()),
    nextCoverageAction: z.string(),
    continuingObligation: ContinuingObligationSchema.optional(),
    lifecycleState: LifecycleState,
    verificationMaturityTier: VerificationMaturityTier,
    verificationNote: z.string().nullable().optional(),
    confidence: z.number().int().min(0).max(100),
    sourceSystem: z.string(),
    lastUpdate: z.string(),
    validationStatus: ValidationStatus,
    evidenceRefIds: z.array(z.string()).optional(),
    reviewerDecisionIds: z.array(z.string()).optional(),
    relatedInquiryIds: z.array(z.string()).optional(),
    // RFC-0003/ADR-0010 D2: marks a card materialised from the runtime promoted_rules
    // store (an LP- rule). Absent = vendored declared-scope baseline. The board renders a
    // "locally promoted · not declared-scope baseline" badge. Honest provenance, NEVER a grade.
    provenanceTier: z.literal("locally_promoted").optional(),
  })
  .superRefine((card, ctx) => {
    if (card.obligationKind === "hard_law") {
      const hasArticleOrDeeper = card.legalReferences.some((ref) =>
        (ARTICLE_OR_DEEPER_GRANULARITIES as ReadonlyArray<string>).includes(
          ref.granularity,
        ),
      );
      if (!hasArticleOrDeeper) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["legalReferences"],
          message:
            "hard_law cards MUST have at least one article-or-deeper legalReference",
        });
      }
    }
    if (
      TIMING_REQUIRES_CONTINUING.includes(card.obligationTiming) &&
      !card.continuingObligation
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["continuingObligation"],
        message: `obligationTiming=${card.obligationTiming} MUST have continuingObligation block`,
      });
    }
  });

export type ComplianceRequirementCard = z.infer<
  typeof ComplianceRequirementCardSchema
>;
